import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { aiConfigApi } from '@/api/client';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';

export function AISettingsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['ai-config'],
    queryFn: () => aiConfigApi.get(),
  });

  const [systemPrompt, setSystemPrompt] = useState('');
  const [offTopicResponse, setOffTopicResponse] = useState('');
  const [escalationThreshold, setEscalationThreshold] = useState(2);
  const [maxHistoryMessages, setMaxHistoryMessages] = useState(10);
  const [enabled, setEnabled] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (data) {
      setSystemPrompt(data.system_prompt);
      setOffTopicResponse(data.off_topic_response ?? '');
      setEscalationThreshold(data.escalation_threshold);
      setMaxHistoryMessages(data.max_history_messages);
      setEnabled(data.enabled);
      setHasChanges(false);
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: aiConfigApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-config'] });
      setHasChanges(false);
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      system_prompt: systemPrompt,
      off_topic_response: offTopicResponse || null,
      escalation_threshold: escalationThreshold,
      max_history_messages: maxHistoryMessages,
      enabled,
    });
  };

  const handleChange = () => {
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <Layout title="AI Настройки">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="AI Настройки" subtitle="Настройка поведения разговорного AI">
      {/* Enable/Disable toggle */}
      <div className="cyber-card p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-neon-cyan/10 flex items-center justify-center">
              <Bot className="w-5 h-5 text-neon-cyan" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-white">Разговорный AI</h3>
              <p className="text-xs text-gray-500">
                Позволяет боту отвечать на вопросы в свободной форме
              </p>
            </div>
          </div>
          <button
            className={`cyber-button text-sm ${enabled ? 'cyber-button-success' : 'cyber-button-danger'}`}
            onClick={() => {
              setEnabled(!enabled);
              handleChange();
            }}
          >
            {enabled ? 'Включён' : 'Выключен'}
          </button>
        </div>
      </div>

      {/* System Prompt */}
      <div className="cyber-card p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-display text-white">Системный промпт</h3>
          <span className="text-xs text-gray-500">{systemPrompt.length} / 5000 символов</span>
        </div>
        <textarea
          value={systemPrompt}
          onChange={(e) => {
            setSystemPrompt(e.target.value);
            handleChange();
          }}
          className="w-full bg-cyber-dark border border-cyber-light rounded-lg px-3 py-2 text-sm text-white focus:border-neon-cyan focus:outline-none min-h-[300px] font-mono"
          placeholder="Инструкции для AI..."
          maxLength={5000}
        />
        <p className="text-xs text-gray-500 mt-2">
          Определяет личность и поведение бота. Используйте русский язык.
        </p>
      </div>

      {/* Off-topic Response */}
      <div className="cyber-card p-4 mb-4">
        <h3 className="text-sm font-display text-white mb-3">Ответ на нерелевантные вопросы</h3>
        <textarea
          value={offTopicResponse}
          onChange={(e) => {
            setOffTopicResponse(e.target.value);
            handleChange();
          }}
          className="w-full bg-cyber-dark border border-cyber-light rounded-lg px-3 py-2 text-sm text-white focus:border-neon-cyan focus:outline-none min-h-[80px]"
          placeholder="Извините, я могу помочь только с вопросами о приложении Kino..."
          maxLength={500}
        />
        <p className="text-xs text-gray-500 mt-2">
          Сообщение, когда пользователь спрашивает не по теме
        </p>
      </div>

      {/* Behavior Settings */}
      <div className="cyber-card p-4 mb-4">
        <h3 className="text-sm font-display text-white mb-4">Параметры поведения</h3>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-300">Порог эскалации</label>
              <span className="text-sm text-neon-cyan">{escalationThreshold}</span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              value={escalationThreshold}
              onChange={(e) => {
                setEscalationThreshold(parseInt(e.target.value));
                handleChange();
              }}
              className="w-full accent-neon-cyan"
            />
            <p className="text-xs text-gray-500 mt-1">
              После скольких неудачных ответов предлагать создать заявку
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-300">Глубина истории</label>
              <span className="text-sm text-neon-cyan">{maxHistoryMessages}</span>
            </div>
            <input
              type="range"
              min={3}
              max={20}
              value={maxHistoryMessages}
              onChange={(e) => {
                setMaxHistoryMessages(parseInt(e.target.value));
                handleChange();
              }}
              className="w-full accent-neon-cyan"
            />
            <p className="text-xs text-gray-500 mt-1">
              Сколько сообщений помнить в контексте разговора
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
          className="cyber-button cyber-button-primary flex items-center gap-2 disabled:opacity-50"
        >
          {updateMutation.isPending ? (
            <LoadingSpinner size="sm" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Сохранить изменения
        </button>
      </div>

      {updateMutation.isSuccess && (
        <div className="mt-4 p-3 bg-neon-green/10 border border-neon-green/30 rounded-lg">
          <p className="text-sm text-neon-green">Настройки сохранены</p>
        </div>
      )}

      {updateMutation.isError && (
        <div className="mt-4 p-3 bg-neon-red/10 border border-neon-red/30 rounded-lg">
          <p className="text-sm text-neon-red">Ошибка сохранения настроек</p>
        </div>
      )}
    </Layout>
  );
}
